import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useFeatureFlag } from '@/hooks/useFeatureFlag';

import { semenApi } from './api';
import {
  AddSemenInput,
  AddSemenToShipmentInput,
  AddSemenTransactionInput,
  AddSemenTransactionV2Input,
  AddStorageLocationInput,
  EditSemenInput,
  RemoveSemenFromShipmentInput,
  SemenTransactionQueryDto,
} from './types';

export const createSemenOptions = (
  searchParams: URLSearchParams = new URLSearchParams(),
  useV3: boolean = false,
) => ({
  queryKey: ['semen', 'list', searchParams.toString(), useV3 ? 'v3' : 'v2'],
  queryFn: () => semenApi.getSemen(searchParams, useV3),
});

export const useSemen = (query: { semenId?: string } = {}) => {
  const useV3 = useFeatureFlag('Semen_Data_Points');

  const searchParams = new URLSearchParams();
  if (query.semenId) {
    searchParams.append('id', query.semenId);
  }
  return useQuery({
    ...createSemenOptions(searchParams, useV3),
    enabled: true,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

export const useAddSemen = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: AddSemenInput) => semenApi.addSemen(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['semen'] });
    },
  });
};

export const useAddStorageLocation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: AddStorageLocationInput) =>
      semenApi.addStorageLocation(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['storageLocation'] });
    },
  });
};

export const useEditSemen = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (inputs: EditSemenInput[]) => semenApi.editSemen(inputs),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['semen'] });
    },
  });
};

export const createSemenTransactionOptions = (
  searchParams: URLSearchParams = new URLSearchParams(),
) => ({
  queryKey: ['semen', 'transactions', searchParams.toString()],
  queryFn: () => semenApi.getSemenTransactionsBySemenIdV2(searchParams),
});

export const useSemenTransactionsV2 = (
  query: SemenTransactionQueryDto = {},
  options?: { enabled?: boolean },
) => {
  const searchParams = new URLSearchParams();
  if (query.semenId) {
    searchParams.append('semenId', query.semenId);
  }
  return useQuery({
    ...createSemenTransactionOptions(searchParams),
    enabled: options?.enabled ?? true,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

export const useSemenTransactions = (
  query: SemenTransactionQueryDto = {},
  options?: { enabled?: boolean },
) => {
  return useQuery({
    queryKey: ['semen', 'transactions', query],
    queryFn: () => semenApi.getSemenTransactionsBySemenId(query),
    enabled: options?.enabled ?? true,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
};

export const useAddSemenTransactionV2 = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: AddSemenTransactionV2Input) =>
      semenApi.addSemenTransactionV2(input),
    onSuccess: (_, variables) => {
      if (variables.transactions) {
        variables.transactions.forEach((transaction) => {
          queryClient.invalidateQueries({
            queryKey: ['semen', 'transactions', transaction.semenId],
          });
        });
      }
      queryClient.invalidateQueries({ queryKey: ['semen', 'transactions'] });
      queryClient.invalidateQueries({ queryKey: ['semen'] });
    },
  });
};

export const useAddSemenTransaction = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: AddSemenTransactionInput) =>
      semenApi.addSemenTransaction(input),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['semen', 'transactions', variables.semenId],
      });
      queryClient.invalidateQueries({ queryKey: ['semen', 'transactions'] });
      queryClient.invalidateQueries({ queryKey: ['semen'] });
    },
  });
};

export const useFertilizationsBySemenId = (semenId: string) => {
  return useQuery({
    queryKey: ['semen', 'fertilizations', semenId],
    queryFn: () => semenApi.getFertilizationsBySemenId(semenId),
    enabled: Boolean(semenId),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

export const useAddSemenToShipment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: AddSemenToShipmentInput) =>
      semenApi.addSemenToShipment(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['semen', 'shipments'] });
    },
  });
};

export const useRemoveSemenFromShipment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: RemoveSemenFromShipmentInput) =>
      semenApi.removeSemenFromShipment(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['semen', 'shipments'] });
    },
  });
};
